import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import {
  toast,
  Flex,
  Modal,
  ModalContext,
  Spinner,
  Text,
  ViewerContext,
  UpgradeSubscriptionModalType,
} from "dash-ui";
import { SubscriptionProductModel, RecurringTypesEnum } from "dash-common";

import { usePaymentConfirmation } from "common/hooks/use-payment-confirmation/use-payment-confirmation";
import {
  SubscriptionProduct,
  useCreateCheckoutSessionMutation,
  useGetSubscriptionProductsQuery,
} from "gql/generated.graphql";

import { IUpgradeSubscriptionModalProps } from "./UpgradeSubscriptionModal.interfaces";
import { RecurringTypeToggle } from "pages/ProfilePage/components/ProfileBilling/components/SubscriptionProductsSection/components";
import {
  getProductNameSlug,
  isHigherPricePerMonth,
  isLowerPricePerMonth,
} from "common/helpers";
import { InvoicePreviewModal } from "pages/ProfilePage/components/ProfileBilling/components";
import SubscriptionProductCard from "v2/components/billing/SubscriptionProductCard/SubscriptionProductCard";

export const CardContainer = styled(Flex)`
  flex-direction: row;
  margin-top: ${({ theme }) => theme.space * 4}px;
  @media ${({ theme }) => theme.devices.mobile} {
    flex-direction: column;
    align-items: center;
    margin-top: ${({ theme }) => theme.space * 4}px;
  }
`;

const UpgradeSubscriptionModal = ({
  type = UpgradeSubscriptionModalType.GENERAL,
  open,
  onClose,
}: IUpgradeSubscriptionModalProps) => {
  // Hooks
  const theme = useTheme();
  const { t } = useTranslation("pages");
  const { isOpenCurrent, toggleModal, payload, setPayload } =
    useContext(ModalContext);
  const { subscription } = useContext(ViewerContext);
  const currentUserProduct = subscription?.subscriptionProduct;

  const [_paymentConfirmation] = usePaymentConfirmation({
    onSuccess: () => {
      closeModal();
      toast.success(t("pages:profilePage.billing.successfulUpgradeMessage"));
      if (payload?.onCompleted) {
        payload.onCompleted();
      }
    },
  });

  // tes
  const [recurringType, setRecurringType] = useState(RecurringTypesEnum.MONTH);

  // Derived States
  const isOpen = isOpenCurrent["UpgradeSubscriptionModal"];

  const closeModal = () => {
    if (onClose) {
      return onClose();
    }
    toggleModal(false, "UpgradeSubscriptionModal");
  };

  // Queries and mutations
  const { data, loading } = useGetSubscriptionProductsQuery({
    skip: !isOpen && !open,
  });
  const subscriptions = data?.getSubscriptionProducts || [];

  const [createCheckoutSession, { loading: checkoutLoading }] =
    useCreateCheckoutSessionMutation();

  // Event handlers
  const handleToggleRecurringType = (newRecurringType: RecurringTypesEnum) => {
    setRecurringType(newRecurringType);
  };

  const { title, subtitle } = useMemo(() => {
    switch (type) {
      case UpgradeSubscriptionModalType.PORTFOLIO:
        return {
          title: t("pages:profilePage.billing.upgradePortfolioPlanModalTitle"),
          subtitle: (
            <Text color={theme.colors.n80}>
              {t("pages:profilePage.billing.upgradePlanModalPortfolioSubtitle")}
            </Text>
          ),
        };
      case UpgradeSubscriptionModalType.DOWNLOAD:
        return {
          title: t("pages:profilePage.billing.upgradeDownloadPlanModalTitle"),
          subtitle: (
            <Text color={theme.colors.n80}>
              {t("pages:profilePage.billing.upgradePlanModalDownloadSubtitle")}
            </Text>
          ),
        };

      default:
        return {
          title: t("pages:profilePage.billing.upgradePlanModalTitle"),
          subtitle: <></>,
        };
    }
  }, [type, t]);

  async function handleSelectPlan(
    product: SubscriptionProduct,
    withTrial?: boolean
  ) {
    // Create a new subscription (in case is a trial or basic -> paid)
    if (!currentUserProduct || currentUserProduct?.price === 0 || withTrial) {
      const productSlug = getProductNameSlug(product);
      const data = await createCheckoutSession({
        variables: {
          input: {
            priceId: product.stripePriceId,
            productId: product.stripeProductId,
            successUrl: `${window.location.href}&subscription=${productSlug}`,
            cancelUrl: window.location.href,
          },
        },
      });

      const sessionUrl = data.data?.createCheckoutSession.sessionUrl;

      return window.open(sessionUrl, "_self");
    }

    const isCurrent = product.name === currentUserProduct?.name;

    // Update an existing subscription
    let mode: "downgrade" | "upgrade" = "upgrade";
    if (isHigherPricePerMonth(product, currentUserProduct)) {
      mode = isCurrent ? "downgrade" : "upgrade";
    } else if (isLowerPricePerMonth(product, currentUserProduct)) {
      mode = isCurrent ? "upgrade" : "downgrade";
    }

    setPayload({
      newSubscriptionProductId: product._id,
      newStripeProductId: product.stripeProductId,
      newStripePriceId: product.stripePriceId,
      mode,
    });
    toggleModal(true, "InvoicePreviewModal");
  }

  return (
    <Modal
      closeIcon
      isOpen={open || isOpen}
      onRequestClose={closeModal}
      title={title}
      viewHierarchy={1}
      maxWidth={"90%"}
      w="fit-content">
      <div className="text-wrap max-w-3xl xl:max-w-none">{subtitle}</div>
      <div className="flex flex-col w-full items-center">
        <Flex alignItems="center" gap={10} my={4}>
          <RecurringTypeToggle
            recurringType={recurringType}
            onToggle={handleToggleRecurringType}
          />
          <Text color={theme.colors.b50} bold>
            Save up to 10%!
          </Text>
        </Flex>
        <div className="w-full flex flex-col gap-4 flex-grow justify-center items-center xl:flex-row xl:items-stretch">
          {!loading &&
            subscriptions
              .filter(
                (product) =>
                  product.recurringType === recurringType ||
                  product.recurringType === RecurringTypesEnum.FREE
              )
              .map((product) => (
                <SubscriptionProductCard
                  key={product._id}
                  product={product as SubscriptionProduct}
                  onSelectPlan={handleSelectPlan}
                  loading={checkoutLoading}
                  animated
                  isCurrent={
                    product._id === subscription?.subscriptionProductId
                  }
                />
              ))}
          {loading && (
            <Flex w="100%" alignItems="center" justifyContent="center">
              <Spinner />
            </Flex>
          )}
        </div>
      </div>
      <InvoicePreviewModal />
    </Modal>
  );
};

export default UpgradeSubscriptionModal;
